import _isNumber from "lodash/isNumber";
import _identity from "lodash/identity";
import _pickBy from "lodash/pickBy";
import { AccountType, ICheckboxGroupValue } from "component-library";
import { getFacetIds } from "shared/helpers/facetHelpers";
import { mapCheckboxValueToList } from "shared/helpers/mappers";
import { IFacetGroup } from "shared/models/facet";
import {
  IAgentJobSettings,
  IPerformerJobSettings,
} from "shared/models/settings";

import { IConverter } from "shared/services/api/types";
import { IJobFilterParams } from "models/api";

import { JobItemType } from "models/job";

import jobService, { IInteractionItem } from "services/api/job/jobService";

import {
  DEFAULT_PAGE_NUMBER,
  FilterType,
  IFilterModel,
  OpenClosedRoles,
  showTypeToJobItemIsSuggestedMapping,
  showTypeToJobItemReadStatusMapping,
  sortingOptionsToSortFilterMapping,
} from "hoc/withJobsFilter/withJobsFilter.config";
import { JobTableItemUuid } from "components/JobsTable/JobsTable.models";

export class IFilterModelToJobFilterParamsConverterOptions {
  contractTypes: IFacetGroup[];
  productionTypes: IFacetGroup[];
  userType: AccountType;
  jobSettings?: IPerformerJobSettings | IAgentJobSettings;
}

export class FilterModelToJobFilterParamsConverter
  implements IConverter<IFilterModel, IJobFilterParams>
{
  constructor(private options: IFilterModelToJobFilterParamsConverterOptions) {}

  public convert(filterModel: IFilterModel): IJobFilterParams {
    const dateSent = filterModel[FilterType.DateSent] ?? [];

    const defaultMinPlayingAge = _isNumber(
      this.options.jobSettings?.minPlayingAge
    )
      ? this.options.jobSettings?.minPlayingAge
      : null;

    const minPlayingAge = _isNumber(filterModel[FilterType.PlayingAgeMin])
      ? filterModel[FilterType.PlayingAgeMin]
      : defaultMinPlayingAge;

    const defaultMaxPlayingAge = _isNumber(
      this.options.jobSettings?.maxPlayingAge
    )
      ? this.options.jobSettings?.maxPlayingAge
      : null;

    const maxPlayingAge = _isNumber(filterModel[FilterType.PlayingAgeMax])
      ? filterModel[FilterType.PlayingAgeMax]
      : defaultMaxPlayingAge;

    const filters = {
      productionLocationIds: filterModel[FilterType.Locations].map(
        (location) => location.value
      ),
      pageNumber: filterModel[FilterType.PageNumber] ?? DEFAULT_PAGE_NUMBER,
      textFilter: filterModel[FilterType.TextFilter] || undefined,
      sentFrom: dateSent[0],
      sentTo: dateSent[1],
      minPlayingAge,
      maxPlayingAge,
      maximumHeight: null,
      minimumHeight: null,
      productionTypes: this.convertCheckboxValue(
        filterModel[FilterType.ProductionType],
        this.options.productionTypes
      ),
      contractTypes: this.convertCheckboxValue(
        filterModel[FilterType.ContractType],
        this.options.contractTypes
      ),
      senderId: filterModel[FilterType.Sender]?.id,
      readStatus:
        showTypeToJobItemReadStatusMapping[filterModel[FilterType.ShowType]],
      sort: sortingOptionsToSortFilterMapping[
        filterModel[FilterType.SortingType]
      ],
      hideClosed: filterModel?.openClosedRoles === OpenClosedRoles.openRoles,
      isSuggested:
        showTypeToJobItemIsSuggestedMapping[filterModel[FilterType.ShowType]],
    };

    const keepZeroAsTruthy = (value: unknown) =>
      value !== undefined && value !== null && value !== "";

    return {
      ...filters,
      ..._pickBy(this.getJobSettingsParams(), _identity),
      ..._pickBy(filters, keepZeroAsTruthy),
    };
  }

  private convertCheckboxValue(
    checkboxValue: ICheckboxGroupValue,
    facetsGroups: IFacetGroup[]
  ): number[] {
    const selectedValues = mapCheckboxValueToList(checkboxValue);

    return !selectedValues.length ? getFacetIds(facetsGroups) : selectedValues;
  }

  private getJobSettingsParams(): Partial<IJobFilterParams> {
    const { jobSettings } = this.options;

    if (!jobSettings) {
      return {};
    }

    return {
      ...(this.isPerformerJobSettings(jobSettings) && {
        genderIds: jobSettings.anyGender ? [] : jobSettings.genderIds,
        appearanceIds: jobSettings.anyAppearance
          ? []
          : jobSettings.appearanceIds,
        disabilityPreference: jobSettings.disabilityPreference,
        minPlayingAge: jobSettings.minPlayingAge,
        maxPlayingAge: jobSettings.maxPlayingAge,
        minimumHeight: jobSettings.minimumHeight,
        maximumHeight: jobSettings.maximumHeight,
        ethnicBackgroundIds: jobSettings.anyEthnicity
          ? []
          : jobSettings.ethnicBackgroundIds,
        heritageAndEthnicityIds: jobSettings.anyEthnicity
          ? []
          : jobSettings.heritageAndEthnicityIds,
      }),
      castingCountries: jobSettings.anyCastingCountry
        ? []
        : jobSettings.castingCountries,
      productionCountries: jobSettings.anyProductionCountry
        ? []
        : jobSettings.productionCountries,
    };
  }

  private isPerformerJobSettings(
    settings: IPerformerJobSettings | IAgentJobSettings
  ): settings is IPerformerJobSettings {
    return this.options.userType === AccountType.Performer;
  }
}

export class JobTableItemUuidToInteractionItemConverter
  implements IConverter<JobTableItemUuid, IInteractionItem>
{
  public convert(uuid: JobTableItemUuid): IInteractionItem {
    const arr = uuid.split("_");
    const linkmailId = arr[1];
    const roleOrFollowUpId = arr[2];
    const jobItemType = Number(arr[0]);
    let id: string;

    switch (jobItemType) {
      case JobItemType.Role:
        id = jobService.generateInteractionItemId(
          JobItemType.Role,
          linkmailId,
          roleOrFollowUpId
        );
        break;
      case JobItemType.FollowUp:
        id = jobService.generateInteractionItemId(
          JobItemType.FollowUp,
          roleOrFollowUpId
        );
        break;
      case JobItemType.Linkmail:
        id = jobService.generateInteractionItemId(
          JobItemType.Linkmail,
          linkmailId
        );
        break;
      default:
        throw new RangeError("Unexpected JobItemType");
    }

    return { id, jobItemType };
  }
}
